/**
 * Queries do subsistema genérico de Forms.
 *
 * Hooks consumidos hoje:
 *
 *   **Rota pública `/onboarding/:token`** (M1-01b-ii.b.2):
 *   - `usePublicSubmission(token)` — GET-like: busca template +
 *     submission via edge function `read-onboarding-submission`.
 *   - `useAutosaveSubmission()` — mutation que faz auto-save do
 *     `partial_answers` via `submit-onboarding-answer` action=autosave.
 *   - `useSubmitSubmission()` — mutation do submit final via
 *     `submit-onboarding-answer` action=submit.
 *
 *   **Superadmin — detalhe do lead** (PR-M1-01e):
 *   - `useLeadFormSubmissions(leadId)` — lista todas as submissões
 *     (qualquer `form_type`) associadas a um lead, com o schema do
 *     template já embutido para renderização via `FormAnswersView`.
 *
 * Quando M1-01d-iii expandir para dispatch genérico (pesquisas com
 * clínicas, satisfação de paciente), hooks análogos entrarão aqui
 * ou em feature modules consumers (`features/surveys`, etc.) que
 * reusam os contratos das edge functions.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { supabase } from '../../lib/supabase.js';

import { formSchemaSchema, type FormSchema } from './schemas.js';

// ─── Shape do payload das edge functions ────────────────────────────
//
// Shape idêntico ao que `supabase/functions/read-onboarding-submission`
// devolve. O `schema` é um jsonb opaco aqui — o componente
// `FormRenderer` faz o parse via `formSchemaSchema`.
const publicSubmissionResponseSchema = z.object({
  mode: z.enum(['live', 'stub']).optional(),
  submission: z.object({
    id: z.string(),
    status: z.enum(['pending', 'in_progress', 'submitted', 'expired']),
    expires_at: z.string(),
    partial_answers: z.record(z.string(), z.unknown()).default({}),
    answers: z.record(z.string(), z.unknown()).nullable(),
    submitted_at: z.string().nullable(),
  }),
  template: z.object({
    id: z.string(),
    name_pt_br: z.string(),
    version: z.number(),
    schema: z.unknown(), // FormSchema opaco — renderer valida
  }),
});

export type PublicSubmissionResponse = z.infer<typeof publicSubmissionResponseSchema>;

const submitAnswerResponseSchema = z.object({
  status: z.enum(['saved', 'submitted']),
  submission_id: z.string(),
  mode: z.enum(['live', 'stub']).optional(),
});

export type SubmitAnswerResponse = z.infer<typeof submitAnswerResponseSchema>;

// ─── Submissões por lead (Superadmin) ────────────────────────────────
//
// Shape do SELECT join de `form_submissions` + `form_templates`. O
// `schema` do template vem como `unknown` — o consumer (UI) parseia
// via `formSchemaSchema` quando for renderizar. Não parseamos aqui
// para não perder a lista inteira se UM template tiver schema inválido.
const leadFormSubmissionStatusSchema = z.enum(['pending', 'in_progress', 'submitted', 'expired']);

export type LeadFormSubmissionStatus = z.infer<typeof leadFormSubmissionStatusSchema>;

const leadFormSubmissionSchema = z.object({
  id: z.string().uuid(),
  status: leadFormSubmissionStatusSchema,
  submitted_at: z.string().nullable(),
  updated_at: z.string(),
  created_at: z.string(),
  expires_at: z.string(),
  template_version: z.number().int().nonnegative(),
  answers: z.record(z.string(), z.unknown()).nullable().default({}),
  partial_answers: z.record(z.string(), z.unknown()).nullable().default({}),
  template: z.object({
    id: z.string().uuid(),
    slug: z.string(),
    form_type: z.string(),
    name_pt_br: z.string(),
    version: z.number().int().nonnegative(),
    // Schema bruto — parse sob demanda no consumer.
    schema: z.unknown(),
  }),
});

export type LeadFormSubmission = z.infer<typeof leadFormSubmissionSchema>;

/** Tenta parsear o schema de um template. Retorna `null` em caso de
 * falha — UI deve cair em fallback "template corrompido, veja JSON
 * cru". Separado para o consumer não precisar importar Zod. */
export function parseTemplateSchema(raw: unknown): FormSchema | null {
  const parsed = formSchemaSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

// ─── Keys ────────────────────────────────────────────────────────────
export const FORMS_KEYS = {
  all: () => ['forms'] as const,
  publicSubmission: (token: string) => ['forms', 'public-submission', token] as const,
  leadSubmissions: (leadId: string) => ['forms', 'lead-submissions', leadId] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────

/**
 * Busca a submission pública pelo token. Não requer autenticação do
 * lead — a edge function resolve via `service_role` (token é o
 * segredo). Cache de 30s: reloads rápidos não recarregam a tela; após
 * 30s considera que o estado pode ter mudado (expired, por ex).
 */
export function usePublicSubmission(
  token: string | undefined,
  options?: Partial<UseQueryOptions<PublicSubmissionResponse, Error>>,
) {
  return useQuery<PublicSubmissionResponse, Error>({
    queryKey: FORMS_KEYS.publicSubmission(token ?? ''),
    enabled: Boolean(token),
    staleTime: 30_000,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('read-onboarding-submission', {
        body: { token },
      });
      if (error) throw new Error(error.message);
      return publicSubmissionResponseSchema.parse(data);
    },
    ...options,
  });
}

/** Auto-save de respostas parciais. Chamado pelo hook `useAutosave`
 * com debounce — cada invoke mata a resposta anterior via React Query
 * mutation semantic (única pending por vez). */
export function useAutosaveSubmission() {
  return useMutation<
    SubmitAnswerResponse,
    Error,
    { token: string; partial_answers: Record<string, unknown> }
  >({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke('submit-onboarding-answer', {
        body: {
          action: 'autosave',
          token: input.token,
          partial_answers: input.partial_answers,
        },
      });
      if (error) throw new Error(error.message);
      return submitAnswerResponseSchema.parse(data);
    },
  });
}

/** Submit final — grava `answers`, marca submitted, transita
 * `leads.stage → form_answered` no backend.
 *
 * `onSuccess` invalida `usePublicSubmission(token)` pra forçar refetch
 * e mover o `OnboardingFormPage` pra tela de confirmação imediatamente.
 * Sem isso, o user via a tela do formulário ainda aparecer depois do
 * submit e clicava "Enviar" de novo (bug 2026-04-24, que disparava um
 * segundo POST e recebia 409 `already_submitted`). */
export function useSubmitSubmission() {
  const qc = useQueryClient();
  return useMutation<
    SubmitAnswerResponse,
    Error,
    { token: string; answers: Record<string, unknown> }
  >({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke('submit-onboarding-answer', {
        body: {
          action: 'submit',
          token: input.token,
          answers: input.answers,
        },
      });
      if (error) throw new Error(error.message);
      return submitAnswerResponseSchema.parse(data);
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: FORMS_KEYS.publicSubmission(variables.token) });
    },
  });
}

/**
 * Lista submissões de um lead — usado no detalhe do lead (Superadmin)
 * para mostrar respostas do questionário. Inclui o schema do
 * `form_templates` via embed relacional para renderização via
 * `FormAnswersView`.
 *
 * **Ordenação**: submetidas primeiro (mais recentes no topo), depois
 * não-submetidas (pending/in_progress/expired) por `updated_at` desc.
 * Karla vê respostas antes de rascunhos.
 *
 * **RLS**: a policy `form_submissions_superadmin_all` (migration 0040)
 * permite SELECT irrestrito para superadmin. Não-superadmin recebe
 * 0 linhas — componente consumidor deve cobrir com skeleton + empty
 * state (não há erro). RLS cobre; sem checagem explícita aqui.
 *
 * **Cache**: 30s (igual `usePublicSubmission`). Reload rápido não
 * refaz; depois disso refresca — se outro admin for adicionar mais
 * contextos (proposta aceita → stage muda, etc), 30s é aceitável.
 */
export function useLeadFormSubmissions(leadId: string | undefined) {
  return useQuery<LeadFormSubmission[], Error>({
    queryKey: FORMS_KEYS.leadSubmissions(leadId ?? ''),
    enabled: Boolean(leadId),
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('form_submissions')
        .select(
          `
            id, status, submitted_at, updated_at, created_at, expires_at,
            template_version, answers, partial_answers,
            template:form_templates!inner (
              id, slug, form_type, name_pt_br, version, schema
            )
          `,
        )
        .eq('lead_id', leadId ?? '')
        .order('submitted_at', { ascending: false, nullsFirst: false })
        .order('updated_at', { ascending: false });

      if (error) throw new Error(error.message);

      // Supabase embed relacional devolve a FK como array ou single.
      // Aqui `!inner` + FK única garante objeto; normalizamos para
      // array-of-objects caso o client devolva array (defensivo).
      const normalized = (data ?? []).map((row) => {
        const rawTemplate = (row as { template: unknown }).template;
        const template = Array.isArray(rawTemplate) ? rawTemplate[0] : rawTemplate;
        return { ...row, template };
      });

      return z.array(leadFormSubmissionSchema).parse(normalized);
    },
  });
}
